import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ToastService } from '@iamsoftware/react-hooks';
import { ProgressSpinner } from 'primereact/progressspinner';

import { AuthService } from 'src/service/AuthService';
import { AppStorage } from 'src/service/UserStorage';

const SSO_REDIRECT_URI_KEY = 'iam-econtract-sso-redirect-uri';
const SSO_PERMISSION_DENIED_KEY = 'iam-econtract-sso-permission-denied';

const cleanupAuthStorage = () => {
  AppStorage.remove('accessToken');
  AppStorage.remove('refreshToken');
  AppStorage.remove('accessTokenExpireAt');
  AppStorage.remove('refreshTokenExpireAt');
  AppStorage.remove('moquiSessionToken');
  AppStorage.remove('partyTaxId');
  sessionStorage.removeItem(SSO_REDIRECT_URI_KEY);
  sessionStorage.setItem(SSO_PERMISSION_DENIED_KEY, 'true');
};

/**
 * Thực hiện SSO logout an toàn.
 * - Thử gọi backend ssoLogout trước (cần externalUserId).
 * - Nếu fail, fallback bằng cách lấy Keycloak login URL, parse thành logout URL,
 *   và redirect trực tiếp đến Keycloak để xóa phiên SSO.
 * @returns true nếu đã redirect (caller nên return ngay), false nếu logout thành công tại chỗ.
 */
const performSsoLogoutSafely = async (): Promise<boolean> => {
  try {
    await AuthService.ssoLogout();
    return false;
  } catch (_) {
    return false;
  }
};

const getCallbackParam = (searchParams: URLSearchParams, key: string) => {
  return searchParams.get(key) || new URLSearchParams(window.location.search).get(key);
};

export default function SsoCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const calledRef = useRef(false);

  useEffect(() => {
    const code = getCallbackParam(searchParams, 'code');
    const sessionState = getCallbackParam(searchParams, 'session_state') || undefined;
    const error = getCallbackParam(searchParams, 'error');
    const errorDescription = getCallbackParam(searchParams, 'error_description');

    const doExchange = async () => {
      if (calledRef.current) return;
      calledRef.current = true;

      if (error) {
        ToastService.error(errorDescription || 'Đăng nhập SSO thất bại');
        navigate('/login', { replace: true });
        return;
      }

      if (!code) {
        ToastService.error('Không tìm thấy mã xác thực SSO');
        navigate('/login', { replace: true });
        return;
      }

      try {
        const tokenData = await AuthService.getToken();
        if (tokenData?.moquiSessionToken) {
          AppStorage.set('moquiSessionToken', tokenData.moquiSessionToken);
        }

        const redirectUri = sessionStorage.getItem(SSO_REDIRECT_URI_KEY) || window.location.origin;
        const data = await AuthService.exchangeSsoCode(code, sessionState, redirectUri);
        sessionStorage.removeItem(SSO_REDIRECT_URI_KEY);

        if (data.isSuccess === 'Y' || data.success === 'Y') {
          AppStorage.set('moquiSessionToken', data.moquiSessionToken);
          if (data.accessToken) {
            AppStorage.set('accessToken', data.accessToken);
          }
          if (data.refreshToken) {
            AppStorage.set('refreshToken', data.refreshToken);
          }
          if (data.accessTokenExpireAt) {
            AppStorage.set('accessTokenExpireAt', String(data.accessTokenExpireAt));
          }
          if (data.refreshTokenExpireAt) {
            AppStorage.set('refreshTokenExpireAt', String(data.refreshTokenExpireAt));
          }

          try {
            const user = await AuthService.getMe();
            if (user?.partyTaxId) {
              AppStorage.set('partyTaxId', user.partyTaxId);
            }
          } catch (e: any) {
            if (e?.response?.status === 403) {
              cleanupAuthStorage();
              const redirected = await performSsoLogoutSafely();
              if (!redirected) {
                ToastService.error('Tài khoản không có quyền truy cập hệ thống. Vui lòng liên hệ quản trị viên.');
                navigate('/login', { replace: true });
              }
              return;
            }
          }

          navigate('/', { replace: true });
        } else {
          cleanupAuthStorage();
          const redirected = await performSsoLogoutSafely();
          if (!redirected) {
            ToastService.error(data.errorMsg || data.messages || 'Đăng nhập SSO thất bại');
            navigate('/login', { replace: true });
          }
        }
      } catch (e) {
        cleanupAuthStorage();
        const redirected = await performSsoLogoutSafely();
        if (!redirected) {
          ToastService.error('Có lỗi xảy ra trong quá trình xác thực SSO');
          navigate('/login', { replace: true });
        }
      }
    };

    doExchange();
  }, [navigate, searchParams]);

  return (
    <div className="flex align-items-center justify-content-center min-h-screen min-w-screen surface-ground relative overflow-hidden">
      <style>{`
        @keyframes pulse-glow {
          0% { transform: scale(0.95); opacity: 0.3; }
          50% { transform: scale(1.05); opacity: 0.7; }
          100% { transform: scale(0.95); opacity: 0.3; }
        }
        @keyframes float-ambient {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .ambient-blob-1 {
          position: absolute;
          width: 450px;
          height: 450px;
          border-radius: 50%;
          background: var(--primary-color);
          opacity: 0.08;
          filter: blur(60px);
          top: -100px;
          left: -100px;
          animation: float-ambient 8s infinite ease-in-out;
        }
        .ambient-blob-2 {
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: #2196f3;
          opacity: 0.06;
          filter: blur(60px);
          bottom: -150px;
          right: -150px;
          animation: float-ambient 10s infinite ease-in-out reverse;
        }
      `}</style>

      {/* Ambient glowing blobs */}
      <div className="ambient-blob-1" />
      <div className="ambient-blob-2" />

      <div className="flex flex-column align-items-center justify-content-center z-1 px-3 w-full">
        <div className="mb-5 fadein animation-duration-1000">
          <img src="/iam/iam-econtract.png" alt="IAM-ECONTRACT" className="flex-shrink-0 block" style={{ width: '18rem' }} />
        </div>

        <div className="fadeinup animation-duration-1000 w-full" style={{ 
          maxWidth: '420px',
          borderRadius: '42px', 
          padding: '0.25rem', 
          background: 'linear-gradient(180deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 100%)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.08)'
        }}>
          <div className="surface-card py-6 px-4 sm:px-6 text-center flex flex-column align-items-center justify-content-center relative w-full" style={{ borderRadius: '40px' }}>
            
            <div className="relative mb-4 flex align-items-center justify-content-center" style={{ width: '84px', height: '84px' }}>
              <div className="absolute top-0 left-0 right-0 bottom-0 border-circle" style={{
                border: '3px solid var(--primary-color)',
                animation: 'pulse-glow 2s infinite ease-in-out'
              }}></div>
              
              <ProgressSpinner style={{ width: '56px', height: '56px' }} strokeWidth="5" fill="none" animationDuration="1s" />
            </div>

            <div className="text-900 text-2xl font-semibold mb-2">Đăng nhập thành công</div>
            <p className="text-600 font-medium m-0 line-height-3 text-sm">
              Hệ thống đang đồng bộ phiên làm việc SSO.<br />Vui lòng đợi trong giây lát...
            </p>

            <div className="mt-5 pt-3 border-top-1 surface-border w-full flex align-items-center justify-content-center gap-2">
              <i className="pi pi-shield text-primary" style={{ fontSize: '1rem' }}></i>
              <span className="text-500 font-medium text-xs">Kết nối an toàn & bảo mật</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

